import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    alpha,
    Box,
    ButtonBase,
    CircularProgress,
    Grid,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import ExpandLessRounded from "@mui/icons-material/ExpandLessRounded";
import dayjs from "dayjs";
import Location, { DayCountSummary } from "../../../api/Location";
import { useCookies } from "react-cookie";
import { FONTS, GLASS_SX } from "../../../constants/styles";

interface Props {
    selectedDay: dayjs.Dayjs;
    setSelectedDay: (val: dayjs.Dayjs) => void;
    onSwitchToWeek: () => void;
    onScroll: (y: number) => void;
}

const MonthView = (props: Props) => {
    const today = dayjs();
    const startOfMonth = today.startOf("month");
    const { selectedDay, setSelectedDay, onSwitchToWeek, onScroll } = props;

    const [months, setMonths] = useState<dayjs.Dayjs[]>([
        startOfMonth,
        startOfMonth.add(1, "month"),
        startOfMonth.add(2, "month"),
    ]);

    const ref = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(false);
    const [prevLoading, setPrevLoading] = useState(false);

    const loadMoreMonths = useCallback(() => {
        if (loading) return;
        setLoading(true);

        setTimeout(() => {
            setMonths((currentMonths) => {
                const lastMonth = currentMonths[currentMonths.length - 1];
                const newMonths = [
                    ...currentMonths,
                    lastMonth.add(1, "month"),
                    lastMonth.add(2, "month"),
                    lastMonth.add(3, "month"),
                ];

                const uniqueMonths = [
                    ...currentMonths,
                    ...newMonths.filter(
                        (newMonth) =>
                            !currentMonths.some((month) =>
                                month.isSame(newMonth, "month")
                            )
                    ),
                ];
                return uniqueMonths;
            });
            setLoading(false);
        }, 500);
    }, [loading]);

    const loadPreviousMonths = useCallback(() => {
        if (!ref.current || prevLoading) return;
        const container = ref.current;

        setPrevLoading(true);

        setTimeout(() => {
            setMonths((currentMonths) => {
                const firstMonth = currentMonths[0];
                const newMonths = [
                    firstMonth.subtract(3, "month"),
                    firstMonth.subtract(2, "month"),
                    firstMonth.subtract(1, "month"),
                ];

                const uniqueMonths = [
                    ...newMonths.filter(
                        (newMonth) =>
                            !currentMonths.some((month) =>
                                month.isSame(newMonth, "month")
                            )
                    ),
                    ...currentMonths,
                ];

                return uniqueMonths;
            });

            setTimeout(() => {
                if (!ref.current) return;
                ref.current.scrollTop = container.scrollTop;
                setPrevLoading(false);
            }, 0);
        }, 500);
    }, [prevLoading]);

    const handleScroll = useCallback(() => {
        if (!ref.current) return;
        const { scrollTop, scrollHeight, clientHeight } = ref.current;
        onScroll(scrollTop);
        if (scrollTop + clientHeight >= scrollHeight - 50) {
            loadMoreMonths();
        }
    }, [loadMoreMonths, onScroll]);

    const handleDaySelection = useCallback(
        (day: dayjs.Dayjs) => {
            setSelectedDay(day);
            sessionStorage.setItem("calendarSelectedDay", day.toISOString());
        },
        [setSelectedDay]
    );

    useEffect(() => {
        const container = ref.current;
        if (!container) return;

        container.addEventListener("scroll", handleScroll);
        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    return (
        <Stack
            className="force-dynamic-translation"
            direction="column"
            width="100%"
            flex={1}
            sx={{
                overflowY: "auto",
                overflowX: "hidden",
                bgcolor: "transparent",
                pt: "72px",
            }}
            ref={ref}
        >
            {/* Toggle pills */}
            <Stack
                direction="row"
                justifyContent="center"
                spacing={1}
                py={2}
            >
                <ButtonBase
                    onClick={onSwitchToWeek}
                    sx={{
                        borderRadius: 20,
                        px: 2,
                        py: 0.75,
                        bgcolor: "transparent",
                    }}
                >
                    <Typography
                        fontFamily={FONTS.display}
                        fontSize="13px"
                        fontWeight={600}
                        color="rgba(255,255,255,0.6)"
                    >
                        Week
                    </Typography>
                </ButtonBase>
                <ButtonBase
                    sx={{
                        borderRadius: 20,
                        px: 2,
                        py: 0.75,
                        bgcolor: "rgba(255,255,255,0.25)",
                    }}
                >
                    <Typography
                        fontFamily={FONTS.display}
                        fontSize="13px"
                        fontWeight={600}
                        color="white"
                    >
                        Month
                    </Typography>
                </ButtonBase>
            </Stack>

            {/* "Show previous" chip */}
            <Stack alignItems="center" mb={1}>
                {prevLoading ? (
                    <CircularProgress
                        size={20}
                        sx={{ color: "rgba(255,255,255,0.7)" }}
                    />
                ) : (
                    <ButtonBase
                        onClick={loadPreviousMonths}
                        sx={{
                            borderRadius: 20,
                            px: 1.5,
                            py: 0.75,
                            bgcolor: "rgba(255,255,255,0.15)",
                        }}
                    >
                        <ExpandLessRounded
                            sx={{
                                fontSize: 16,
                                color: "white",
                                mr: 0.75,
                            }}
                        />
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="13px"
                            fontWeight={600}
                            color="white"
                        >
                            Previous months
                        </Typography>
                    </ButtonBase>
                )}
            </Stack>

            {/* Month cards */}
            <Stack
                spacing={2}
                px={2}
                pb="120px"
                sx={{
                    "@keyframes fadeSlideIn": {
                        from: {
                            opacity: 0,
                            transform: "translateY(12px)",
                        },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                }}
            >
                {months.map((x, index) => (
                    <Box
                        key={x.format("YYYY-MM")}
                        sx={{
                            animation: "fadeSlideIn 0.35s ease-out both",
                            animationDelay: `${200 + index * 60}ms`,
                        }}
                    >
                        <MonthItem
                            month={x}
                            selected={selectedDay}
                            today={today}
                            onSelect={handleDaySelection}
                        />
                    </Box>
                ))}
                {loading && (
                    <CircularProgress
                        size={20}
                        sx={{
                            alignSelf: "center",
                            my: 2,
                            color: "rgba(255,255,255,0.7)",
                        }}
                    />
                )}
            </Stack>
        </Stack>
    );
};

const MonthItem = (props: {
    month: dayjs.Dayjs;
    selected: dayjs.Dayjs;
    today: dayjs.Dayjs;
    onSelect: (val: dayjs.Dayjs) => void;
}) => {
    const { month, selected, today, onSelect } = props;

    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const [availableDays, setAvailableDays] = useState<DayCountSummary[]>([]);
    const [content, setContent] = useState<(dayjs.Dayjs | undefined)[]>([]);

    useEffect(() => {
        let day = month;
        let month_days: (dayjs.Dayjs | undefined)[] = [];

        Location.bookingsMonth(
            uuid,
            brand_id,
            location_id,
            month.get("year"),
            month.get("month") + 1
        )
            .then((res: DayCountSummary[]) => setAvailableDays(res))
            .catch(console.error);

        for (let i = 1; i < (day.get("day") === 0 ? 7 : day.get("day")); i++) {
            month_days.push(undefined);
        }

        while (day.get("month") === month.get("month")) {
            month_days.push(day);
            day = day.add(1, "day");
        }

        setContent(month_days);
    }, [uuid, brand_id, location_id, month]);

    return (
        <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            width="100%"
            py={2}
            sx={{
                ...GLASS_SX,
                borderRadius: "16px",
                p: 2,
                boxSizing: "border-box",
            }}
        >
            <Stack
                direction="row"
                width="90%"
                alignItems="center"
                justifyContent="space-between"
            >
                <Typography
                    fontFamily={FONTS.display}
                    fontWeight={600}
                    fontSize="14px"
                    color="rgba(255,255,255,0.8)"
                >
                    {month.format("MMMM, YYYY")}
                </Typography>
            </Stack>
            <Grid container columns={7} width="95%" rowSpacing={1}>
                {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((x) => (
                    <Grid
                        key={x}
                        item
                        xs={1}
                        display="flex"
                        flexDirection="row"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Typography
                            fontWeight={600}
                            fontSize="12px"
                            color="rgba(255,255,255,0.6)"
                        >
                            {x}
                        </Typography>
                    </Grid>
                ))}
                {content.map((x, index) => {
                    if (x === undefined) {
                        return (
                            <Grid
                                key={index}
                                item
                                xs={1}
                                display="flex"
                                flexDirection="row"
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Box width="48px" height="48px" />
                            </Grid>
                        );
                    }

                    const summary: DayCountSummary | undefined =
                        availableDays[x.get("date") - 1];

                    return (
                        <Grid
                            key={index}
                            item
                            xs={1}
                            display="flex"
                            flexDirection="row"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <DayView
                                day={x}
                                selected={
                                    x.format("DD.MM.YYYY") ===
                                    selected?.format("DD.MM.YYYY")
                                }
                                current={
                                    x.format("DD.MM.YYYY") ===
                                    today.format("DD.MM.YYYY")
                                }
                                onClick={() => onSelect(x)}
                                summary={summary}
                                onGradient
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </Stack>
    );
};

export const DayView = (props: {
    day: dayjs.Dayjs;
    selected: boolean;
    current: boolean;
    onClick: () => void;
    summary?: DayCountSummary;
    onGradient?: boolean;
}) => {
    const theme = useTheme();
    const hex = theme.palette.primary.main;

    const { day, selected, current, onClick, summary, onGradient = false } =
        props;

    const isClosed = summary?.count === -1;

    const selectedRef = useRef<null | HTMLDivElement>(null);

    const items = (summary?.items ?? []).slice(0, 14);

    // Determine styles based on state + context
    const getStyles = () => {
        if (onGradient) {
            if (selected) {
                return {
                    bgcolor: "white",
                    textColor: hex,
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
                };
            }
            if (current) {
                return {
                    bgcolor: "rgba(255,255,255,0.2)",
                    textColor: "white",
                };
            }
            if (isClosed) {
                return {
                    bgcolor: "transparent",
                    textColor: "rgba(255,255,255,0.4)",
                };
            }
            return {
                bgcolor: "transparent",
                textColor: "rgba(255,255,255,0.9)",
            };
        }
        // On white
        if (selected) {
            return {
                bgcolor: hex,
                textColor: "white",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
            };
        }
        if (current) {
            return {
                bgcolor: alpha(hex, 0.08),
                textColor: hex,
            };
        }
        return {
            bgcolor: "transparent",
            textColor: "text.primary",
        };
    };

    const styles = getStyles();

    // Count bookings vs blocked for summary indicators
    const bookingCount = items.filter((it) => !it.isBlocked).length;
    const blockedCount = items.filter((it) => it.isBlocked).length;

    return (
        <ButtonBase
            sx={{
                width: "48px",
                minWidth: "48px",
                maxWidth: "48px",
                height: "48px",
                borderRadius: "50%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: styles.bgcolor,
                boxShadow: styles.boxShadow,
                opacity: isClosed && !onGradient ? 0.3 : 1,
                transition: "background-color 0.15s, box-shadow 0.15s",
            }}
            onClick={onClick}
        >
            {selected && <div ref={selectedRef} />}
            <Typography
                fontWeight={selected ? 700 : 400}
                fontSize="16px"
                lineHeight="20px"
                color={styles.textColor}
            >
                {day.get("date")}
            </Typography>
            <Indicators
                bookingCount={bookingCount}
                blockedCount={blockedCount}
                onGradient={onGradient}
                selected={selected}
            />
        </ButtonBase>
    );
};

const Indicators = (props: {
    bookingCount: number;
    blockedCount: number;
    onGradient: boolean;
    selected: boolean;
}) => {
    const theme = useTheme();
    const hex = theme.palette.primary.main;
    const { bookingCount, blockedCount, onGradient, selected } = props;

    const hasAny = bookingCount > 0 || blockedCount > 0;

    // Color logic: on gradient = white variants, on white = brand/grey variants
    // Selected inverts: on-gradient selected has white bg so use brand colors,
    // on-white selected has brand bg so use white
    const bookingColor = onGradient
        ? selected ? hex : "white"
        : selected ? "white" : hex;
    const blockedColor = onGradient
        ? selected ? alpha(hex, 0.35) : "rgba(255,255,255,0.45)"
        : selected ? "rgba(255,255,255,0.5)" : alpha("#000", 0.25);

    return (
        <Stack
            direction="row"
            height="10px"
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
        >
            {hasAny ? (
                <>
                    {bookingCount > 0 && (
                        <Box
                            sx={{
                                width: bookingCount > 1 ? 12 : 6,
                                height: 4,
                                borderRadius: "2px",
                                bgcolor: bookingColor,
                                opacity: onGradient && !selected ? 0.85 : 1,
                                transition: "width 0.15s",
                            }}
                        />
                    )}
                    {blockedCount > 0 && (
                        <Box
                            sx={{
                                width: blockedCount > 1 ? 12 : 6,
                                height: 4,
                                borderRadius: "2px",
                                bgcolor: blockedColor,
                                transition: "width 0.15s",
                            }}
                        />
                    )}
                </>
            ) : null}
        </Stack>
    );
};

export default MonthView;
